import { useEffect, useRef, useState } from 'react'
import { ScrollstoryDocumentDataBodyFoldOutSlicePrimary } from '../../../types.generated.ts'
import { PrismicRichText } from '@prismicio/react'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'
import { BREAKPOINT_MOBILE } from '../../util/breakpoints.ts'
import { getLinkFieldUrl } from '../../data/prismic-helper.ts'
import { BaseCaption, InlineElementContainer } from '../atoms/Atoms.tsx'
import { asText } from '@prismicio/client'
import { LazyImage } from '../atoms/LazyImage.tsx'
import { VideoPlayer } from '../atoms/video/VideoPlayer.tsx'

export interface InlineFoldoutProps {
  data: ScrollstoryDocumentDataBodyFoldOutSlicePrimary
  lang: string
}

export const InlineFoldout = (props: InlineFoldoutProps) => {
  const [isFoldedOut, setIsFoldedOut] = useState(false)
  const [contentHeight, setContentHeight] = useState(0)

  const theme = useTheme()
  const contentRef = useRef<HTMLDivElement>(null)

  const toggleFoldout = () => {
    setIsFoldedOut(!isFoldedOut)
  }

  useEffect(() => {
    const updateContentHeight = () => {
      if (contentRef.current) {
        const height = contentRef.current.scrollHeight
        setContentHeight(height)
      }
    }
    updateContentHeight()

    if (isFoldedOut) {
      window.addEventListener('resize', () => updateContentHeight())
    }

    return () => {
      window.removeEventListener('resize', () => updateContentHeight())
    }
  }, [isFoldedOut])

  const videoUrl = getLinkFieldUrl(props.data.video)

  const caption = asText(props.data.media_caption)
  const source = asText(props.data.media_source)

  return (
    <FoldoutContainer>
      <Header onClick={toggleFoldout} aria-expanded={isFoldedOut}>
        <TitleWrapper>{props.data.title}</TitleWrapper>
        <IconWrapper isFoldedOut={isFoldedOut}>
          <OpenCloseIcon iconSrc={theme.foldoutIcon} />
        </IconWrapper>
      </Header>
      <ContentWrapper
        ref={contentRef}
        isFoldedOut={isFoldedOut}
        contentHeight={contentHeight}
      >
        <Image
          src={props.data.image.url ?? ''}
          alt={props.data.image.alt ?? ''}
        />
        {videoUrl && (
          <VideoPlayer
            videoUrl={videoUrl}
            playing={false}
            controls={'browserDefault'}
            muted={false}
            loop={false}
            subtitleLang={props.lang}
            alt={props.data.video_alt_text ?? ''}
          />
        )}
        {(caption || source) && (
          <Description>
            <FoldoutCaption>
              {caption} <i>{source}</i>
            </FoldoutCaption>
          </Description>
        )}
        <Text>
          <PrismicRichText field={props.data.text} />
        </Text>
      </ContentWrapper>
    </FoldoutContainer>
  )
}

const ADDITIONAL_FOLDOUT_WIDTH_EM = 1.2
const FoldoutContainer = styled(InlineElementContainer)`
  display: flex;
  flex-direction: column;
  justify-content: center;

  width: calc(
    ${({ theme }) => theme.inlineTextMaxWidth} +
      ${ADDITIONAL_FOLDOUT_WIDTH_EM * 2}em
  );
  max-width: 100%;

  padding: 0 20px;
  @media ${BREAKPOINT_MOBILE} {
    width: 100%;
    padding: 0 24px;
  }

  background-color: #221f1f1a;
  ${({ theme }) => theme.foldoutContainerCss}
`

const Header = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1em 0;
  border: none;
  width: 100%;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  text-align: left;
  background-color: transparent;

  &:focus {
    outline: none;
    box-shadow: none;
  }
`

const ContentWrapper = styled.div<{
  isFoldedOut: boolean
  contentHeight: number
}>`
  max-height: ${({ isFoldedOut, contentHeight }) =>
    isFoldedOut ? `${contentHeight}px` : '0'};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: max-height 0.3s ease-in-out;
  padding-bottom: ${({ isFoldedOut }) => (isFoldedOut ? '1em' : '0')};
`

const Image = styled(LazyImage)`
  width: 100%;
  height: auto;
  object-fit: cover;
`

const Description = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);
  ${({ theme }) => theme.foldoutCaptionCss}
`

const FoldoutCaption = styled(BaseCaption)`
  @media ${BREAKPOINT_MOBILE} {
    padding: 0;
  }
`

const Text = styled.div`
  ${({ theme }) => theme.foldoutTextCss}
`

const IconWrapper = styled.div<{ isFoldedOut: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${({ isFoldedOut }) =>
    isFoldedOut ? 'rotate(0deg)' : 'rotate(180deg)'};
  transition: transform 300ms ease-in-out;
`

const TitleWrapper = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  ${({ theme }) => theme.foldoutTitleCss}
`

interface OpenCloseIconProps {
  iconSrc: string | null | undefined
}

const OpenCloseIcon = (props: OpenCloseIconProps) => {
  return props.iconSrc ? (
    <img src={props.iconSrc ?? ''} alt="foldout open and close icon" />
  ) : (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M24 19.0654C24 19.3039 23.9099 19.5435 23.7282 19.7262C23.3661 20.0913 22.7802 20.0913 22.4182 19.7262L16.0001 13.2549L9.58154 19.7262C9.21949 20.0913 8.63307 20.0913 8.27154 19.7262C7.90949 19.3612 7.90949 18.77 8.27154 18.405L15.3446 11.2738C15.7066 10.9087 16.2931 10.9087 16.6546 11.2738L23.7281 18.405C23.9094 18.5872 24 18.8263 24 19.0654Z"
        fill="#221F1F"
      />
    </svg>
  )
}
